import React, { useState, useEffect, useMemo } from 'react';
import { getSquadPillarScores, getPillarWeights } from '../api';
import PillarBubbleSpider from '../components/PillarBubbleSpider';
import PageChrome from '../components/PageChrome';

const PILLAR_KEYS = ['physical', 'technical', 'tactical', 'identity', 'availability'];
const DEFAULT_WEIGHTS = { physical: 30, technical: 30, tactical: 20, identity: 10, availability: 10 };

// Average of all active players' scores for a pillar, excluding NULLs from the average
// rather than counting them as 0 — same "no data ≠ 0" principle as everywhere else in this
// build. Null (not 0) if literally no active player has data for this pillar yet.
function squadAverage(rows, key) {
  const values = rows.map(r => r[key]).filter(v => v != null);
  if (!values.length) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

// Mirrors Stage 3's per-player overall formula exactly (pillarScores.js's
// computeOverallAndStars), just fed squad averages instead of one player's pillars — so the
// squad's star rating derives from the same weighted-average-of-non-null-pillars math.
// Weights are still fetched/used here purely for this calculation — the new bubble layout's
// sizing is fixed per-pillar (see PillarBubbleSpider's PILLAR_LAYOUT), not weight-driven.
function computeOverallAndStars(pillarAverages, weights) {
  const physicalForOverall = pillarAverages.physical == null ? null : Math.min(pillarAverages.physical, 100);
  const entries = PILLAR_KEYS.map(k => [k, k === 'physical' ? physicalForOverall : pillarAverages[k], weights[k]])
    .filter(([, val, w]) => val != null && w != null);
  // Same minimum-coverage gate as pillarScores.js's per-player formula (2026-07-22) — a
  // single present pillar renormalizes to 100% of the weight, which can fake a squad-level
  // rating off one pillar alone.
  if (entries.length < 2) return { overall: null, stars: null };
  const totalWeight = entries.reduce((s, [, , w]) => s + w, 0);
  const weightedSum = entries.reduce((s, [, val, w]) => s + val * w, 0);
  const overall = totalWeight > 0 ? weightedSum / totalWeight : null;
  const stars = overall == null ? null : Math.round((overall / 100 * 5) * 2) / 2;
  return { overall, stars };
}

export default function Home({ user, onNavigate }) {
  const [period, setPeriod] = useState('week');
  const [rows, setRows] = useState([]);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPillarWeights()
      .then(rows => {
        if (!rows?.length) return;
        const w = {};
        rows.forEach(r => { w[r.pillar] = Number(r.weight); });
        setWeights(w);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getSquadPillarScores(period)
      .then(data => {
        const n = v => (v === null || v === undefined ? null : Number(v));
        setRows((data || []).map(r => ({
          physical: n(r.physical), technical: n(r.technical), tactical: n(r.tactical),
          identity: n(r.identity), availability: n(r.availability),
        })));
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  const squadPillars = useMemo(() => {
    const out = {};
    PILLAR_KEYS.forEach(k => { out[k] = squadAverage(rows, k); });
    return out;
  }, [rows]);

  const { stars } = useMemo(() => computeOverallAndStars(squadPillars, weights), [squadPillars, weights]);

  // Gated the same way as everywhere else (user.isAdmin || allowedTabs.includes(id)) — these
  // bubbles used to link through unconditionally regardless of allowedTabs, the same class of
  // gap tabs.js's HOME_ONLY_TABS/PILLAR_BUBBLE_TABS comments describe (fixed 2026-07-29). A
  // bubble with no bubbleLinks[key] entry just renders non-clickable (PillarBubbleSpider's own
  // existing `clickable = typeof onClick === 'function'` check) — same graceful "not available
  // to you" degradation Technical's bubble already had before its page was built.
  const canAccess = tabId => !!(user?.isAdmin || (user?.allowedTabs || []).includes(tabId));
  const bubbleLinks = {};
  if (canAccess('overview')) bubbleLinks.physical = () => onNavigate('overview');
  if (canAccess('availability')) bubbleLinks.availability = () => onNavigate('availability-analysis');
  if (canAccess('tactical-overview')) bubbleLinks.tactical = () => onNavigate('tactical-overview');
  if (canAccess('identity-overview')) bubbleLinks.identity = () => onNavigate('identity-overview');
  if (canAccess('technical-overview')) bubbleLinks.technical = () => onNavigate('technical-overview');

  return (
    <PageChrome onNavigate={onNavigate} user={user} contentMaxWidth={500} padding="16px 32px 24px">
      {/* Home-only spacing below the nav bar — PillarBubbleSpider itself stays untouched so
          Individual Report (which also renders it directly under PageChrome's TopBar) doesn't
          pick up the same offset. */}
      <div style={{ marginTop: 28 }}>
        {loading ? (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
        ) : (
          <PillarBubbleSpider
            centerImageUrl={`${process.env.PUBLIC_URL}/heracles-crest.png`}
            centerInitial="H"
            title="Heracles Almelo"
            subtitle="Squad average"
            stars={stars}
            pillars={squadPillars}
            period={period}
            onPeriodChange={setPeriod}
            bubbleLinks={bubbleLinks}
            bottomReserve={24}
          />
        )}
      </div>
    </PageChrome>
  );
}
